import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

/** Durations the app already used, kept as named constants so call sites read clearly. */
export const NOTIFY_SHORT_MS = 3000;
export const NOTIFY_MEDIUM_MS = 4000;
export const NOTIFY_LONG_MS = 5000;

/**
 * Single entry point for transient user feedback.
 *
 * Wraps MatSnackBar so the dismiss label and duration defaults live in one
 * place instead of being repeated at every call site.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly snackBar = inject(MatSnackBar);

  /** Confirmation of a completed action. */
  success(message: string, durationMs: number = NOTIFY_SHORT_MS): void {
    this.show(message, durationMs);
  }

  /** A failure the user should notice; defaults to a longer read time. */
  error(message: string, durationMs: number = NOTIFY_MEDIUM_MS): void {
    this.show(message, durationMs);
  }

  /** Neutral information. */
  info(message: string, durationMs: number = NOTIFY_SHORT_MS): void {
    this.show(message, durationMs);
  }

  show(
    message: string,
    durationMs: number = NOTIFY_SHORT_MS,
    dismissLabel = 'Fechar',
    verticalPosition?: 'top' | 'bottom',
  ): void {
    // `verticalPosition` is left off unless asked for: MatSnackBar already
    // defaults to 'bottom', and omitting it keeps the config identical to what
    // the call sites passed before they were routed through this service.
    this.snackBar.open(
      message,
      dismissLabel,
      verticalPosition ? { duration: durationMs, verticalPosition } : { duration: durationMs },
    );
  }
}
